/**
 * Admin Panel - Deletion Policy API
 *
 * Zod schemas for the system-wide deletion mode toggle (per entity).
 * Stored in SystemConfig under category="deletion", one row per entity:
 *   - key="agent.policy"      → applies to Agent deletion
 *   - key="deployment.policy" → applies to Deployment deletion
 *
 * Each entity is independently SOFT or HARD. SOFT keeps rows with a
 * deletedAt marker (reversible via restore). HARD snapshots a tombstone
 * with rendering metadata only and removes the row (irreversible).
 *
 * The setting affects FUTURE deletions only — existing deleted records
 * keep their original shape.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { DeletionModeSchema } from "../system-config/schemas/deletion-policy-config.schema";

// ============================================================
// Schemas
// ============================================================

/** Current effective policy for both entities. */
export const DeletionPolicyResponseSchema = z.object({
  agent: DeletionModeSchema,
  deployment: DeletionModeSchema
});

/**
 * Patch body. At least one of `agent` or `deployment` must be set.
 * Either entity can be updated independently in a single request.
 */
export const UpdateDeletionPolicyBodySchema = z
  .object({
    agent: DeletionModeSchema.optional(),
    deployment: DeletionModeSchema.optional()
  })
  .refine((b) => b.agent !== undefined || b.deployment !== undefined, {
    message: "At least one of `agent` or `deployment` must be provided"
  });

// ============================================================
// ZAdminDeletionPolicy - Endpoint Definitions
// ============================================================

export const ZAdminDeletionPolicy = {
  /**
   * Get the current deletion policy for both entities.
   * GET /api/admin/deletion-policy
   */
  GetDeletionPolicy: {
    method: "GET" as const,
    path: "/api/admin/deletion-policy",
    Response: ApiSuccessSchema(DeletionPolicyResponseSchema)
  },

  /**
   * Update the deletion policy for one or both entities.
   * PATCH /api/admin/deletion-policy
   */
  UpdateDeletionPolicy: {
    method: "PATCH" as const,
    path: "/api/admin/deletion-policy",
    Body: UpdateDeletionPolicyBodySchema,
    Response: ApiSuccessSchema(DeletionPolicyResponseSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type DeletionPolicyResponse = z.infer<typeof DeletionPolicyResponseSchema>;
export type UpdateDeletionPolicyBody = z.infer<typeof UpdateDeletionPolicyBodySchema>;
